import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import ColorPickerPopoverContent from "../../newsletter-editor/fields/ColorPickerPopoverContent";
import {
  ARTICLE_DEFAULT_FONT_SIZE,
  ARTICLE_EDITOR_COLOR_SWATCHES,
  ARTICLE_FONT_SIZE_OPTIONS,
} from "../../../../utils/articleEditorColorPalette";
import { contentArticleParagraphToolbarSx } from "../contentEditorStyles";

const ArticleParagraphToolbar = ({
  align,
  boldActive,
  colorAnchorRef,
  colorAnchor,
  colorValue,
  fontSizeValue,
  formatActive,
  italicActive,
  selectionActive,
  onAlignChange,
  onBoldToggle,
  onColorAnchorClose,
  onColorAnchorOpen,
  onColorApply,
  onColorDraftChange,
  onFontSizeChange,
  onItalicToggle,
}) => (
  <Box sx={contentArticleParagraphToolbarSx}>
    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
      <ToggleButtonGroup size="small" value={[]} aria-label="Stile testo">
        <Tooltip
          title={
            selectionActive
              ? "Grassetto (selezione)"
              : formatActive
                ? "Grassetto (testo da inserire)"
                : "Posiziona il cursore nel paragrafo per applicare il grassetto"
          }
        >
          <span>
            <ToggleButton
              value="bold"
              selected={boldActive}
              disabled={!formatActive}
              onMouseDown={(event) => event.preventDefault()}
              onClick={onBoldToggle}
              aria-pressed={boldActive}
            >
              <FormatBoldIcon fontSize="small" />
            </ToggleButton>
          </span>
        </Tooltip>
        <Tooltip
          title={
            selectionActive
              ? "Corsivo (selezione)"
              : formatActive
                ? "Corsivo (testo da inserire)"
                : "Posiziona il cursore nel paragrafo per applicare il corsivo"
          }
        >
          <span>
            <ToggleButton
              value="italic"
              selected={italicActive}
              disabled={!formatActive}
              onMouseDown={(event) => event.preventDefault()}
              onClick={onItalicToggle}
              aria-pressed={italicActive}
            >
              <FormatItalicIcon fontSize="small" />
            </ToggleButton>
          </span>
        </Tooltip>
      </ToggleButtonGroup>

      <Divider flexItem orientation="vertical" sx={{ mx: 0.25, borderColor: "#cbd5e1" }} />

      <Tooltip
        title={
          selectionActive
            ? "Dimensione testo selezionato"
            : formatActive
              ? "Dimensione del testo da inserire"
              : "Posiziona il cursore nel paragrafo per cambiare la dimensione"
        }
      >
        <FormControl size="small" sx={{ minWidth: 92 }} disabled={!formatActive}>
          <InputLabel id="article-paragraph-font-size-label">Dim.</InputLabel>
          <Select
            labelId="article-paragraph-font-size-label"
            label="Dim."
            value={fontSizeValue}
            onChange={(event) => onFontSizeChange(Number(event.target.value))}
            MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
          >
            {ARTICLE_FONT_SIZE_OPTIONS.map((size) => (
              <MenuItem key={size} value={size} sx={{ fontSize: size >= 24 ? "1rem" : "0.875rem" }}>
                {size}px
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Tooltip>

      <Tooltip
        title={
          selectionActive
            ? "Colore testo selezionato"
            : formatActive
              ? "Colore del testo da inserire"
              : "Posiziona il cursore nel paragrafo per cambiare il colore"
        }
      >
        <span>
          <IconButton
            ref={colorAnchorRef}
            size="small"
            disabled={!formatActive}
            onMouseDown={(event) => event.preventDefault()}
            onClick={onColorAnchorOpen}
          sx={{
            border: "1px solid #cbd5e1",
            borderRadius: 1,
            width: 36,
            height: 32,
            flexDirection: "column",
            gap: 0.25,
          }}
        >
          <FormatColorTextIcon sx={{ fontSize: 18, color: "#334155" }} />
          <Box sx={{ width: 18, height: 3, borderRadius: 1, bgcolor: colorValue, border: "1px solid #94a3b8" }} />
        </IconButton>
        </span>
      </Tooltip>

      <Divider flexItem orientation="vertical" sx={{ mx: 0.25, borderColor: "#cbd5e1" }} />

      <ToggleButtonGroup
        size="small"
        exclusive
        value={align || ""}
        onChange={onAlignChange}
        aria-label="Allineamento"
      >
        <Tooltip title={selectionActive ? "Allinea righe selezionate a sinistra" : "Allinea il paragrafo a sinistra"}>
          <ToggleButton value="left" aria-label="Allinea a sinistra">
            <FormatAlignLeftIcon fontSize="small" />
          </ToggleButton>
        </Tooltip>
        <Tooltip title={selectionActive ? "Allinea righe selezionate al centro" : "Allinea il paragrafo al centro"}>
          <ToggleButton value="center" aria-label="Allinea al centro">
            <FormatAlignCenterIcon fontSize="small" />
          </ToggleButton>
        </Tooltip>
        <Tooltip title={selectionActive ? "Giustifica righe selezionate" : "Giustifica il paragrafo"}>
          <ToggleButton value="justify" aria-label="Giustifica">
            <FormatAlignJustifyIcon fontSize="small" />
          </ToggleButton>
        </Tooltip>
      </ToggleButtonGroup>
    </Stack>

    <Popover
      open={Boolean(colorAnchor)}
      anchorEl={colorAnchor}
      onClose={onColorAnchorClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          onMouseDown: (event) => event.preventDefault(),
          sx: {
            p: 1,
            borderRadius: 1.5,
            border: "1px solid #e2e8f0",
            boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
          },
        },
      }}
    >
      <ColorPickerPopoverContent
        compact
        showHexInput
        defaultColor="#1f2937"
        color={colorValue}
        onChange={onColorDraftChange}
        onApply={(nextColor) => {
          onColorApply(nextColor);
          onColorAnchorClose();
        }}
        onSwatchSelect={onColorDraftChange}
        swatches={ARTICLE_EDITOR_COLOR_SWATCHES}
      />
    </Popover>
  </Box>
);

ArticleParagraphToolbar.propTypes = {
  align: PropTypes.oneOfType([PropTypes.oneOf(["left", "center", "justify"]), PropTypes.bool]).isRequired,
  boldActive: PropTypes.bool.isRequired,
  colorAnchorRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]).isRequired,
  colorAnchor: PropTypes.any,
  colorValue: PropTypes.string.isRequired,
  fontSizeValue: PropTypes.number.isRequired,
  formatActive: PropTypes.bool.isRequired,
  italicActive: PropTypes.bool.isRequired,
  selectionActive: PropTypes.bool.isRequired,
  onAlignChange: PropTypes.func.isRequired,
  onBoldToggle: PropTypes.func.isRequired,
  onColorAnchorClose: PropTypes.func.isRequired,
  onColorAnchorOpen: PropTypes.func.isRequired,
  onColorApply: PropTypes.func.isRequired,
  onColorDraftChange: PropTypes.func.isRequired,
  onFontSizeChange: PropTypes.func.isRequired,
  onItalicToggle: PropTypes.func.isRequired,
};

ArticleParagraphToolbar.defaultProps = {
  colorAnchor: null,
  fontSizeValue: ARTICLE_DEFAULT_FONT_SIZE,
};

export default ArticleParagraphToolbar;
